import React, { useState, useEffect } from 'react';
import { ICON } from '../icons/iconIds';
import IconText from './icons/IconText';
import { getAuthorizationService } from '../services/authorizationService';
import { authService } from '../auth/authService';
import { APPLICATIONS } from '../config/appConfig';

const DEFAULT_DOCUMENT_TITLE = 'Wolfwalker - Selkirk Copper';

function documentTitleForRoute(route) {
  const normalized = !route || route === '' ? '/' : route.split('?')[0];
  if (normalized === '/') {
    return `Home | ${DEFAULT_DOCUMENT_TITLE}`;
  }
  const app = Object.values(APPLICATIONS).find((a) => a.path === normalized);
  if (app) {
    return `${app.name} | ${DEFAULT_DOCUMENT_TITLE}`;
  }
  return `Page Not Found | ${DEFAULT_DOCUMENT_TITLE}`;
}

// Import your existing components
import AppLauncher from './AppLauncher';
import HelloWorld from './HelloWorld'; // This will be your Wolfwalker app
import Layout from './layout/Layout';

// Import active applications
import DrillingDashboard from './DrillingDashboard';
import ClaimManagement from './ClaimManagement';
import SampleTracking from './SampleTracking';
import AssayCompositing from './AssayCompositing';
import QAQC from './QAQC';
import DrillingCosts from './DrillingCosts';
import DeviationWizard from './DeviationWizard';
import DrillholeValidation from './DrillholeValidation';
import LabApprovals from './LabApprovals';
import DocuSignConnect from './DocuSignConnect';
import Debug from './Debug';
import WhosIn from './WhosIn';
import DailyReport from './DailyReport';
import DocumentDesign from './DocumentDesign';

const ReportGenerator = ({ user }) => (
  <Layout user={user} currentRoute="/report-generator">
    <div style={{ padding: '2rem', textAlign: 'center', color: '#f9fafb' }}>
      <IconText icon={ICON.appReportGenerator} as="h1">Report Generator</IconText>
      <p>Coming Soon - Automated report generation and templates</p>
    </div>
  </Layout>
);

const QGISViewer = ({ user }) => (
  <Layout user={user} currentRoute="/qgis-viewer">
    <div style={{ padding: '2rem', textAlign: 'center', color: '#f9fafb' }}>
      <IconText icon={ICON.appQgisViewer} as="h1">QGIS Viewer</IconText>
      <p>Coming Soon - Standalone QGIS map viewer</p>
    </div>
  </Layout>
);

const AdminPanel = ({ user }) => (
  <Layout user={user} currentRoute="/admin">
    <div style={{ padding: '2rem', textAlign: 'center', color: '#f9fafb' }}>
      <IconText icon={ICON.appAdministration} as="h1">Administration Panel</IconText>
      <p>Coming Soon - Platform administration and user management</p>
    </div>
  </Layout>
);

const NotFound = () => (
  <div style={{ 
    padding: '2rem', 
    textAlign: 'center', 
    background: '#030712',
    color: '#f9fafb',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  }}>
    <h1>404 - Page Not Found</h1>
    <p>The requested application could not be found.</p>
    <button 
      onClick={() => window.location.hash = '/'}
      style={{
        background: '#f9be0b',
        color: '#111827',
        border: 'none',
        padding: '0.75rem 1.5rem',
        borderRadius: '0.5rem',
        cursor: 'pointer',
        fontWeight: '600'
      }}
    >
      ← Back to Home
    </button>
  </div>
);

const AccessDenied = ({ appName }) => (
  <div style={{ 
    padding: '2rem', 
    textAlign: 'center', 
    background: '#030712',
    color: '#ef4444',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  }}>
    <IconText icon={ICON.uiAccessDenied} as="h1">Access Denied</IconText>
    <p>You don't have permission to access {appName}.</p>
    <p>Please contact your administrator to request access.</p>
    <button 
      onClick={() => window.location.hash = '/'}
      style={{
        background: '#f9be0b',
        color: '#111827',
        border: 'none',
        padding: '0.75rem 1.5rem',
        borderRadius: '0.5rem',
        cursor: 'pointer',
        fontWeight: '600',
        marginTop: '1rem'
      }}
    >
      ← Back to Home
    </button>
  </div>
);

const AppRouter = ({ user, onLogout }) => {
  const [currentRoute, setCurrentRoute] = useState('/');
  const [authorizationService, setAuthorizationService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initError, setInitError] = useState(null);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const authzService = getAuthorizationService(authService);
        await authzService.initializeUserPermissions();
        setAuthorizationService(authzService);
        setInitError(null);
      } catch (error) {
        console.error('Failed to initialize authorization service:', error);
        if (authService.isSessionEndedError(error)) {
          return;
        }
        setInitError(
          error?.message ||
            'Could not load your permissions. Try refreshing the page.'
        );
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  useEffect(() => {
    // Handle route changes
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1) || '/';
      const pathOnly = hash.split('?')[0] || '/';
      setCurrentRoute(pathOnly);
    };

    // Set initial route
    handleHashChange();

    // Listen for hash changes (handles back button and manual navigation)
    window.addEventListener('hashchange', handleHashChange);
    
    // Listen for popstate events (additional browser navigation support)
    const handlePopState = (event) => {
      handleHashChange();
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [authorizationService]); // Re-run when authorization service is ready

  useEffect(() => {
    if (loading) {
      document.title = DEFAULT_DOCUMENT_TITLE;
      return;
    }
    document.title = documentTitleForRoute(currentRoute);
  }, [currentRoute, loading]);

  const renderRoute = () => {
    if (loading) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          background: '#030712',
          color: '#f9fafb'
        }}>
          <div style={{ 
            width: '3rem', 
            height: '3rem', 
            border: '3px solid rgba(156, 163, 175, 0.3)', 
            borderTop: '3px solid #f9be0b', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite',
            marginBottom: '1rem'
          }}></div>
          <p>Loading application...</p>
        </div>
      );
    }

    if (!authorizationService) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          background: '#030712',
          color: initError ? '#ef4444' : '#f9fafb',
          padding: '2rem',
          textAlign: 'center'
        }}>
          {initError ? (
            <>
              <h1>Permissions unavailable</h1>
              <p>{initError}</p>
              <button 
                type="button"
                onClick={() => window.location.reload()}
                style={{
                  background: '#f9be0b',
                  color: '#111827',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontWeight: '600',
                  marginTop: '1rem'
                }}
              >
                Refresh page
              </button>
            </>
          ) : (
            <p>Initializing permissions...</p>
          )}
        </div>
      );
    }

    // Route mapping
    const routes = {
      '/': () => <AppLauncher user={user} />,
      '/wolfwalker': () => {
        if (authorizationService?.hasAccessToApp('wolfwalker')) {
          try {
            return (
              <Layout user={user} onLogout={onLogout} currentRoute="/wolfwalker">
                <div style={{ padding: '2rem', background: '#030712', color: '#f9fafb', minHeight: '100vh' }}>
                  <IconText icon={ICON.appDebug} as="h1">Wolfwalker Debug</IconText>
                  <p>User: {user?.name}</p>
                  <p>Route: /wolfwalker</p>
                  <p>This is a test to see if the route works</p>
                  <HelloWorld user={user} />
                </div>
              </Layout>
            );
          } catch (error) {
            console.error('❌ Error rendering Wolfwalker:', error);
            return (
              <div style={{ padding: '2rem', background: '#030712', color: '#ef4444', minHeight: '100vh' }}>
                <IconText icon={ICON.uiError} as="h1">Wolfwalker Error</IconText>
                <p>Error: {error.message}</p>
              </div>
            );
          }
        }
        return <AccessDenied appName="Wolfwalker" />;
      },
      '/drilling-dashboard': () => {
        if (authorizationService.hasAccessToApp('drilling-dashboard')) {
          return <DrillingDashboard user={user} />;
        }
        return <AccessDenied appName="Drilling Dashboard" />;
      },
      '/claim-management': () => {
        if (authorizationService.hasAccessToApp('claim-management')) {
          return <ClaimManagement user={user} />;
        }
        return <AccessDenied appName="Claim Management" />;
      },
      '/sample-tracking': () => {
        if (authorizationService.hasAccessToApp('sample-tracking')) {
          return <SampleTracking user={user} />;
        }
        return <AccessDenied appName="Sample Tracking" />;
      },
      '/assay-compositing': () => {
        if (authorizationService.hasAccessToApp('assay-compositing')) {
          return <AssayCompositing user={user} />;
        }
        return <AccessDenied appName="Assay Compositing" />;
      },
      '/qaqc': () => {
        if (authorizationService.hasAccessToApp('qaqc')) {
          return <QAQC user={user} />;
        }
        return <AccessDenied appName="QAQC" />;
      },
      '/drilling-costs': () => {
        if (authorizationService.hasAccessToApp('drilling-costs')) {
          return <DrillingCosts user={user} />;
        }
        return <AccessDenied appName="Drilling Costs" />;
      },
      '/deviation-wizard': () => {
        if (authorizationService.hasAccessToApp('deviation-wizard')) {
          return <DeviationWizard user={user} />;
        }
        return <AccessDenied appName="Deviation Wizard" />;
      },
      '/drillhole-validation': () => {
        if (authorizationService.hasAccessToApp('drillhole-validation')) {
          return <DrillholeValidation user={user} />;
        }
        return <AccessDenied appName="Drillhole Validation" />;
      },
      '/lab-approvals': () => {
        if (authorizationService.hasAccessToApp('lab-approvals')) {
          return <LabApprovals user={user} />;
        }
        return <AccessDenied appName="Lab Approvals" />;
      },
      '/docusign': () => {
        if (authorizationService.hasAccessToApp('docusign')) {
          return <DocuSignConnect user={user} />;
        }
        return <AccessDenied appName="DocuSign" />;
      },
      '/debug': () => {
        if (authorizationService.hasAccessToApp('debug')) {
          return <Debug user={user} />;
        }
        return <AccessDenied appName="Debug" />;
      },
      '/whos-in': () => {
        if (authorizationService.hasAccessToApp('whos-in')) {
          return <WhosIn user={user} />;
        }
        return <AccessDenied appName="Who's In?" />;
      },
      '/daily-report': () => {
        if (authorizationService.hasAccessToApp('daily-report')) {
          return <DailyReport user={user} />;
        }
        return <AccessDenied appName="Daily Report" />;
      },
      '/document-design': () => {
        if (authorizationService.hasAccessToApp('document-design')) {
          return <DocumentDesign user={user} />;
        }
        return <AccessDenied appName="Document Design" />;
      },
      '/report-generator': () => {
        if (authorizationService.hasAccessToApp('report-generator')) {
          return <ReportGenerator user={user} />;
        }
        return <AccessDenied appName="Report Generator" />;
      },
      '/qgis-viewer': () => {
        if (authorizationService.hasAccessToApp('qgis-viewer')) {
          return <QGISViewer user={user} />;
        }
        return <AccessDenied appName="QGIS Viewer" />;
      },
      '/admin': () => {
        if (authorizationService.hasAccessToApp('admin')) {
          return <AdminPanel user={user} />;
        }
        return <AccessDenied appName="Administration Panel" />;
      }
    };

    const pathOnly = (currentRoute || '/').split('?')[0] || '/';
    const RouteComponent = routes[pathOnly];
    
    if (!RouteComponent) {
      return <NotFound />;
    }
    
    try {
      return RouteComponent();
    } catch (error) {
      console.error('Error rendering route:', currentRoute, error);
      return (
        <div style={{ 
          padding: '2rem', 
          textAlign: 'center', 
          background: '#030712',
          color: '#f9fafb',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <IconText icon={ICON.uiWarning} as="h1">Application Error</IconText>
          <p>Failed to load route: {pathOnly}</p>
          <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>{error.message}</p>
          <button 
            onClick={() => window.location.hash = '/'}
            style={{
              background: '#f9be0b',
              color: '#111827',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '0.5rem',
              marginTop: '1rem',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            ← Back to Home
          </button>
        </div>
      );
    }
  };

  return (
    <div className="app-router">
      {renderRoute()}
    </div>
  );
};

export default AppRouter;
